import React, { useState } from 'react';

import { Button, Flex } from '@mantine/core';
import { IconClipboardCheck, IconPlayerPlay } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { NoDataFound } from 'components/NoDataFound';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { ExportButton } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ExportButton';
import { EvaluationTestsList } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationView/EvaluationTestsList';
import { EvaluationTestView } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationView/EvaluationTestView';
import { useTranslation } from 'react-i18next';
import { EvaluationService } from 'services/evaluation-service';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { LanguageService } from 'services/language-service';
import { NotificationService } from 'services/notification-service';
import { anyEvaluationHasRunningTests } from 'shared/constants/evaluation';
import { EXPORT_TYPE_TO_MIME_TYPE, ExportType } from 'shared/constants/export';
import { Evaluation, ExportEvaluationDto, ExportEvaluationDtoWithSmartAssistantId } from 'shared/interfaces/Evaluation';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface EvaluationViewProps {
  evaluation: Evaluation;
  smartAssistant: SmartAssistant;
  onBack: () => void;
  handleRunAllTests: (evaluationId: string) => void;
  handleRunSpecificTests: (evaluationTestIds: string[], evaluationId: string) => void;
  isGuest?: boolean;
}

export function EvaluationView({
  evaluation,
  smartAssistant,
  onBack,
  handleRunAllTests,
  handleRunSpecificTests,
  isGuest = false
}: EvaluationViewProps) {
  const { t } = useTranslation();

  const [selectedTestId, setSelectedTestId] = useState<string | null>(
    evaluation.evaluationTests.length > 0 ? evaluation.evaluationTests[0]._id : null
  );

  const exportEvaluationMutation = useMutation({
    mutationFn: (exportEvaluationDto: ExportEvaluationDto | ExportEvaluationDtoWithSmartAssistantId) =>
      (isGuest ? GuestSmartAssistantService : EvaluationService).exportEvaluation(evaluation._id, exportEvaluationDto),
    onSuccess: (buffer: ArrayBuffer, arg: ExportEvaluationDto) => {
      const mimeType = EXPORT_TYPE_TO_MIME_TYPE.get(arg.exportType);
      const blob = new Blob([buffer], { type: mimeType });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        t('SMART_ASSISTANT.EVALUATIONS_SECTION.EXPORT.FILE_NAME', { fileExtension: arg.exportType })
      );
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.EVALUATION.FAILURE.EXPORT')
  });

  const handleExportEvaluation = (type: ExportType) => {
    exportEvaluationMutation.mutate({
      exportType: type,
      locale: LanguageService.getCurrentLanguage(),
      // -1 because getTimezoneOffset returns positive value for negative offset
      timezoneOffsetMs: new Date().getTimezoneOffset() * 60 * 1000 * -1,
      ...(isGuest ? { smartAssistantId: smartAssistant._id } : {})
    });
  };

  // Derive the actual test object from the evaluation prop during render
  // This ensures we always use the latest version from the evaluation prop
  const selectedTest = selectedTestId
    ? evaluation.evaluationTests.find((test) => test._id === selectedTestId) || null
    : evaluation.evaluationTests.length > 0
    ? evaluation.evaluationTests[0]
    : null;

  const displayNoEvaluationTestsFound = evaluation.evaluationTests.length === 0;

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={evaluation.name}
      onBack={onBack}
      extraRightSection={
        <Flex gap="xs">
          <ExportButton
            disabled={anyEvaluationHasRunningTests([evaluation])}
            loading={exportEvaluationMutation.isLoading}
            performExport={handleExportEvaluation}
            size="xs"
          />
          {!isGuest && (
            <Button
              loading={anyEvaluationHasRunningTests([evaluation])}
              size="xs"
              leftIcon={<IconPlayerPlay />}
              onClick={() => handleRunAllTests(evaluation._id)}>
              {t('SMART_ASSISTANT.EVALUATIONS_SECTION.RUN_ALL_EVALUATION_TESTS')}
            </Button>
          )}
        </Flex>
      }>
      <Flex h="100%">
        {displayNoEvaluationTestsFound ? (
          <NoDataFound
            h="100%"
            w="100%"
            icon={<IconClipboardCheck />}
            title={t('NO_DATA_FOUND.EVALUATION_TESTS.TITLE')}
            subTitle={t('NO_DATA_FOUND.EVALUATION_TESTS.SUB_TITLE')}
          />
        ) : (
          <Flex h="100%" w="100%" py="sm" gap="md">
            <EvaluationTestsList
              evaluationTests={evaluation.evaluationTests}
              setSelectedTestId={setSelectedTestId}
              selectedTestId={selectedTestId}
            />
            {selectedTest && (
              <EvaluationTestView
                evaluationTest={selectedTest}
                smartAssistant={smartAssistant}
                handleRunSpecificTests={(evaluationTestIds) =>
                  handleRunSpecificTests(evaluationTestIds, evaluation._id)
                }
                isGuest={isGuest}
              />
            )}
          </Flex>
        )}
      </Flex>
    </ManageSmartAssistantSectionControlWrapper>
  );
}
